import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Video, Clock, DollarSign, Key, CheckCircle, Play, CreditCard, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import StripeInAppCheckout from './StripeInAppCheckout';
import { isMobileBuild } from '@/lib/mobileBuild';
import UpgradeOnWebNotice from './UpgradeOnWebNotice';

const StreamPurchaseModal = ({ 
  isOpen, 
  onClose, 
  stream, 
  onSuccess,
  purchaseType = 'live', // 'live' or 'replay'
  allowTypeSwitch = true
}) => {
  const [purchased, setPurchased] = useState(false);
  const [streamPassword, setStreamPassword] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [selectedType, setSelectedType] = useState(purchaseType);

  useEffect(() => {
    if (isOpen) {
      setSelectedType(purchaseType);
      setShowCheckout(false);
      setPurchased(false);
    }
  }, [isOpen, purchaseType]);

  const handleClose = () => {
    setPurchased(false);
    setStreamPassword(null);
    setShowCheckout(false);
    onClose();
  };

  if (!stream) return null;

  if (isMobileBuild() && !purchased) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Video className="w-5 h-5 text-cyan-500" />
              Purchase {selectedType === 'live' ? 'Live Access' : 'Replay Access'}
            </DialogTitle>
          </DialogHeader>
          <UpgradeOnWebNotice
            title="Get stream access on our website"
            description="Stream access is purchased securely on our website. Buy there once, then watch right here in the app."
            ctaLabel="Purchase on Website"
            path="/dashboard/live-stream"
            testId="stream-purchase-web-notice"
          />
        </DialogContent>
      </Dialog>
    );
  }

  const livePrice = Number(stream.live_price) || 0;
  // Admin may leave replay_price unset — fall back to the live price
  const replayPrice = Number(stream.replay_price) || livePrice;
  const price = selectedType === 'live' ? livePrice : replayPrice;
  // An admin sale is applied by the server at checkout; show it here so a pre-sale
  // is visible before anyone clicks Buy.
  const salePercent = selectedType === 'live'
    ? stream.sale_live_percent : stream.sale_replay_percent;
  const salePrice = selectedType === 'live'
    ? stream.sale_live_price : stream.sale_replay_price;
  const typeLabel = selectedType === 'live' ? 'Live Access' : 'Replay Access';

  const handlePaymentSuccess = () => {
    setPurchased(true);
    setShowCheckout(false);
    if (stream.stream_password) {
      setStreamPassword(stream.stream_password);
    }
    toast.success('Stream access purchased!');
    onSuccess?.();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {selectedType === 'live' ? (
              <Video className="w-5 h-5 text-cyan-500" />
            ) : (
              <Play className="w-5 h-5 text-purple-500" />
            )}
            {purchased ? 'Access Granted!' : `Purchase ${typeLabel}`}
          </DialogTitle>
        </DialogHeader>

        {purchased ? (
          <div className="space-y-4 py-4">
            <div className="text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-green-600 mb-2">Purchase Complete!</h3>
              <p className="text-gray-600">Your access to &quot;{stream.title}&quot; has been confirmed.</p>
            </div>

            {streamPassword && (
              <Card className="bg-slate-800 border-cyan-500">
                <CardContent className="pt-4">
                  <div className="text-center">
                    <p className="text-sm text-gray-400 mb-2">Your Stream Password</p>
                    <div className="flex items-center justify-center gap-2">
                      <Key className="w-5 h-5 text-cyan-400" />
                      <span className="text-2xl font-mono font-bold text-cyan-400 tracking-wider">
                        {streamPassword}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      This password has also been sent to your email.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {selectedType === 'live' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>Bonus:</strong> Your live access includes free replay access once the recording is available!
                </p>
              </div>
            )}

            <Button onClick={handleClose} className="w-full">
              Close
            </Button>
          </div>
        ) : showCheckout ? (
          <div className="py-2">
            <StripeInAppCheckout
              itemType="live_stream"
              itemId={stream.id}
              purchaseType={selectedType}
              onSuccess={handlePaymentSuccess}
              onError={(msg) => toast.error(msg)}
            />
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">{stream.title}</CardTitle>
                <CardDescription className="line-clamp-2">{stream.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <Badge variant={selectedType === 'live' ? 'default' : 'secondary'}>
                    {typeLabel}
                  </Badge>
                  {stream.scheduled_date && (
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <Clock className="w-4 h-4" />
                      {new Date(stream.scheduled_date).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {allowTypeSwitch && (
              <div className="space-y-2">
                <p className="text-sm text-gray-600">Choose what you want</p>
                <button
                  type="button"
                  onClick={() => setSelectedType('live')}
                  className={`w-full text-left rounded-lg border p-3 transition-colors ${
                    selectedType === 'live'
                      ? 'border-cyan-400 bg-cyan-500/10'
                      : 'border-gray-600 hover:border-cyan-400/60'
                  }`}
                  data-testid="stream-option-live"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-semibold flex items-center gap-2">
                      <Video className="w-4 h-4 text-cyan-400" /> Live + Replay
                    </span>
                    <span className="font-bold text-cyan-400">
                      {stream.sale_live_percent ? (
                        <>
                          <span className="text-emerald-700">
                            ${(stream.sale_live_price ?? livePrice).toFixed(2)}
                          </span>
                          <span className="ml-2 text-xs text-gray-600 line-through font-normal">
                            ${livePrice.toFixed(2)}
                          </span>
                        </>
                      ) : `$${livePrice.toFixed(2)}`}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Attend live and keep the recording</p>
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedType('replay')}
                  className={`w-full text-left rounded-lg border p-3 transition-colors ${
                    selectedType === 'replay'
                      ? 'border-purple-400 bg-purple-500/10'
                      : 'border-gray-600 hover:border-purple-400/60'
                  }`}
                  data-testid="stream-option-replay"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-semibold flex items-center gap-2">
                      <Play className="w-4 h-4 text-purple-400" /> Replay Only
                    </span>
                    <span className="font-bold text-purple-400">
                      {stream.sale_replay_percent ? (
                        <>
                          <span className="text-emerald-700">
                            ${(stream.sale_replay_price ?? replayPrice).toFixed(2)}
                          </span>
                          <span className="ml-2 text-xs text-gray-600 line-through font-normal">
                            ${replayPrice.toFixed(2)}
                          </span>
                        </>
                      ) : `$${replayPrice.toFixed(2)}`}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Watch the recording on your own time{stream.has_replay ? '' : ' — unlocks as soon as it is posted'}
                  </p>
                </button>
              </div>
            )}

            <Card className="bg-gradient-to-r from-cyan-900/30 to-purple-900/30 border-cyan-500/50">
              <CardContent className="pt-4">
                <div className="text-center">
                  <p className="text-sm text-gray-700 mb-1">Total Price</p>
                  {salePercent ? (
                    <>
                      <div className="text-4xl font-bold text-emerald-700" data-testid="stream-purchase-total">
                        ${(salePrice ?? price)?.toFixed(2)}
                      </div>
                      <p className="text-sm mt-1" data-testid="stream-purchase-sale">
                        <span className="text-gray-600 line-through">${price?.toFixed(2)}</span>
                        <span className="ml-2 text-emerald-700 font-semibold">{salePercent}% off</span>
                      </p>
                    </>
                  ) : (
                    <div className="text-4xl font-bold text-cyan-400" data-testid="stream-purchase-total">
                      ${price?.toFixed(2)}
                    </div>
                  )}
                  {selectedType === 'live' && (
                    <p className="text-xs text-gray-500 mt-1">Includes free replay access</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Button
              onClick={() => setShowCheckout(true)}
              className="w-full bg-cyan-600 hover:bg-cyan-700 text-white"
              data-testid="stream-purchase-btn"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Continue to Payment
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StreamPurchaseModal;
